import { Card, Header } from "semantic-ui-react";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { LicenceType } from "../models/globalTypes";
import { Licence } from "../models/Licence";

interface Props {
  licences?: Licence[] | null;
  type: LicenceType;
  label: string;
}

interface TimelinePoint {
  ts: number;
  date: string;
  users: number;
  currentUsers: number | null;
  aboveUsers: number | null;
  belowUsers: number | null;
  isoDate: string;
}

const buildTimeline = (
  licences: Licence[],
): { points: TimelinePoint[]; todayTs: number; cutoffTs: number; currentUsedUsers: number } => {
  const activeLicences = licences.filter((l) => l.isActive);
  if (activeLicences.length === 0) return { points: [], todayTs: 0, cutoffTs: 0, currentUsedUsers: 0 };

  // Format a Date as a local YYYY-MM-DD string (avoids UTC shift from toISOString())
  const toLocalDateStr = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // Strip time from any date value coming from the server
  const toDateOnly = (val: any): string => {
    const s = String(val);
    // If already YYYY-MM-DD, use directly; otherwise parse and localise
    return s.length >= 10 && s[4] === "-" ? s.substring(0, 10) : toLocalDateStr(new Date(s));
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = toLocalDateStr(today);

  const cutoff = new Date(today);
  cutoff.setMonth(cutoff.getMonth() + 6);
  const cutoffStr = toLocalDateStr(cutoff);

  // Collect distinct expiry dates (date-only) within the 6-month window
  const expiryDates = Array.from(
    new Set(activeLicences.map((l) => toDateOnly(l.endDate)).filter((d) => d > todayStr && d <= cutoffStr)),
  ).sort();

  // Checkpoints: today + each expiry date + cutoff (always shown even if no expiry then)
  const checkpoints = Array.from(new Set([todayStr, ...expiryDates, cutoffStr]));

  const fmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" });

  // initialUsers = today's total capacity (all active licences).
  const initialUsers = activeLicences.reduce((sum, l) => sum + l.numberOfUsers, 0);
  const currentUsedUsers = initialUsers - activeLicences.reduce((sum, l) => sum + (l.availableUserSlots ?? 0), 0);

  const todayTs = today.getTime();
  const cutoffTs = cutoff.getTime();

  const basePoints = checkpoints.map((isoDate) => {
    const users = activeLicences
      .filter((l) => toDateOnly(l.endDate) > isoDate)
      .reduce((sum, l) => sum + l.numberOfUsers, 0);

    const [y, mo, d] = isoDate.split("-").map(Number);
    const pointDate = new Date(y, mo - 1, d);
    return { ts: pointDate.getTime(), isoDate, date: fmt.format(pointDate), users };
  });

  const split = (users: number) => ({
    aboveUsers: users >= currentUsedUsers ? users : null,
    belowUsers: users < currentUsedUsers ? users : null,
  });

  // The value never changes until the first expiry checkpoint, so that opening
  // plateau is just "current licences" (not a projection) — track it separately.
  const initialValue = basePoints[0]?.users ?? 0;
  const firstChangeIndex = basePoints.findIndex((p) => p.users !== initialValue);
  const projectionStartIndex = firstChangeIndex === -1 ? basePoints.length - 1 : firstChangeIndex;

  const points: TimelinePoint[] = [];

  basePoints.forEach((point, index) => {
    const previous = basePoints[index - 1];
    const isBeforeProjection = index < projectionStartIndex;
    const isProjectionStart = index === projectionStartIndex;

    if (previous && previous.users !== point.users) {
      // Explicit plateau end: hold the previous value right up to this checkpoint's x,
      // colored per its own value so no red segment is drawn when it's at/above current usage.
      const isPlateauBeforeProjection = index - 1 < projectionStartIndex;
      const crossesThreshold =
        !isPlateauBeforeProjection &&
        (previous.users >= currentUsedUsers) !== (point.users >= currentUsedUsers);

      points.push({
        ...previous,
        ts: point.ts,
        isoDate: point.isoDate,
        date: point.date,
        currentUsers: isPlateauBeforeProjection ? previous.users : null,
        ...(isPlateauBeforeProjection
          ? { aboveUsers: null, belowUsers: null }
          : crossesThreshold
            ? // Colour change mid-projection: put the held value on BOTH series so the
              // blue line ends and the red line begins at the exact same point (no gap).
              { aboveUsers: previous.users, belowUsers: previous.users }
            : split(previous.users)),
      });
    }

    points.push({
      ...point,
      currentUsers: isBeforeProjection || isProjectionStart ? point.users : null,
      ...(isBeforeProjection ? { aboveUsers: null, belowUsers: null } : split(point.users)),
    });
  });

  return { points, todayTs, cutoffTs, currentUsedUsers };
};

const LicenceExpiryTimeline = ({ licences, type, label }: Props) => {
  const filtered = licences?.filter((l) => l.type === type) ?? [];
  const { points: data, todayTs, cutoffTs, currentUsedUsers } = buildTimeline(filtered);

  if (data.length < 2) return null;

  const fmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" });
  const tickFormatter = (ts: number) => fmt.format(new Date(ts));
  const tooltipLabelFormatter = (_: any, payload: any[]) => (payload?.[0] ? `Date: ${payload[0].payload.date}` : "");

  return (
    <Card fluid>
      <Card.Content>
        <Header as="h5" textAlign="center">
          {label} — Licensed Users Expiry Timeline
        </Header>
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="ts"
              type="number"
              scale="time"
              domain={[todayTs, cutoffTs]}
              tickFormatter={tickFormatter}
              tick={{ fontSize: 11 }}
            />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
            <Tooltip
              formatter={(value: number, name: string) => [
                value,
                name === "currentUsers" ? "Current licences" : "If not renewed",
              ]}
              labelFormatter={tooltipLabelFormatter}
            />
            <Legend
              payload={[
                { value: "Current licences", type: "line", color: "#767676" },
                { value: "If not renewed", type: "line", color: "#2185d0" },
                { value: "If not renewed", type: "line", color: "#db2828" },
              ]}
              wrapperStyle={{ fontSize: 11 }}
            />
            <ReferenceLine
              x={todayTs}
              stroke="#db2828"
              strokeDasharray="4 4"
              label={{ value: "Today", position: "insideTopRight", fontSize: 11, fill: "#db2828" }}
            />
            <Line
              type="linear"
              dataKey="currentUsers"
              name="currentUsers"
              stroke="#767676"
              strokeWidth={2}
              dot={{ r: 4, fill: "#767676" }}
              activeDot={{ r: 6 }}
              connectNulls={false}
              isAnimationActive={false}
            />
            <Line
              type="linear"
              dataKey="aboveUsers"
              name="If not renewed"
              stroke="#2185d0"
              strokeWidth={2}
              dot={{ r: 4, fill: "#2185d0" }}
              activeDot={{ r: 6 }}
              connectNulls={false}
              isAnimationActive={false}
            />
            <Line
              type="linear"
              dataKey="belowUsers"
              name="If not renewed"
              stroke="#db2828"
              strokeWidth={2}
              dot={{ r: 4, fill: "#db2828" }}
              activeDot={{ r: 6 }}
              connectNulls={false}
              isAnimationActive={false}
            />
            <ReferenceLine
              y={currentUsedUsers}
              stroke="#21ba45"
              strokeWidth={2}
              strokeDasharray="6 4"
              label={{ value: "Current used licences", position: "insideTopLeft", fontSize: 11, fill: "#21ba45" }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Card.Content>
    </Card>
  );
};

export default LicenceExpiryTimeline;
